// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import type { LocationCompanyType } from '@/constants/location-company-type';
import type { SortDirection } from '@/constants/sort';
import { pagedShipmentSchema, statusToUpperCase } from '@/shipment/api/types';
import type { ShipmentStatus, ShipmentType } from '@/shipment/api/types';
import queryString from 'query-string';
import { z } from 'zod';

/**
 * Schema for validating sort properties.
 */
export const shipmentSortPropertySchema = z.enum([
  'shipmentNumber',
  'assetValue',
  'shippingCost',
  'status',
  'timeInStatus',
]);

export type ShipmentSortProperty = z.infer<typeof shipmentSortPropertySchema>;

/**
 * Schema for validating the response from the get shipments API endpoint.
 * Includes both the array of shipment data and metadata about the request.
 * Transforms null data into an empty array for consistent handling.
 */
const shipmentsResponseSchema = z
  .object({
    data: pagedShipmentSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data }) => ({
    shipments: data.results,
    resultCount: data.totalCount,
    needsAttentionCount: data.needsAttentionCount,
    quarantinedCount: data.quarantinedCount,
    statusCounts: data.statusCounts,
  }));

/**
 * Type definition for the get shipments API response.
 * Contains the array of shipments and request metadata.
 */
type ShipmentsResponse = z.infer<typeof shipmentsResponseSchema>;

/**
 * Filter options for shipments.
 *
 * The from/to location filter dimensions mirror the fulfillment listing:
 * each side independently scopes by explicit location IDs, customer IDs
 * (matching the location's assigned customer), and a customer-vs-manufacturer
 * company-type partition.
 */
export interface ShipmentFilter {
  status?: ShipmentStatus;
  shipmentType?: ShipmentType;
  query?: string;
  pageNumber?: number;
  pageSize?: number;
  foNumbers?: string[];
  poNumbers?: string[];
  roNumbers?: string[];
  // Filters for shipments whose origin or destination matches any of the
  // provided location IDs. Used by the location detail "Related shipments"
  // tab to surface shipments moving to or from a specific location.
  locationIds?: string[];
  fromLocationIds?: string[];
  fromCustomerIds?: string[];
  fromLocationCompanyTypes?: LocationCompanyType[];
  toLocationIds?: string[];
  toCustomerIds?: string[];
  toLocationCompanyTypes?: LocationCompanyType[];
  sort?: ShipmentSortProperty;
  direction?: SortDirection;
  // Restricts to shipments with active attention.
  needsAttention?: boolean;
  // Restricts to shipments holding a quarantined serial.
  quarantined?: boolean;
}

/**
 * Fetches shipments with the specified filter criteria.
 */
export async function getShipments(
  filter?: ShipmentFilter
): Promise<ShipmentsResponse> {
  const url = '/v1/shipment';
  const sortByMap: Record<ShipmentSortProperty, string> = {
    shipmentNumber: 'shipment_number',
    assetValue: 'total_value',
    shippingCost: 'shipping_cost_amount',
    status: 'status',
    timeInStatus: 'time_in_status',
  };
  const response = await api.get(url, {
    paramsSerializer: (parameters) => queryString.stringify(parameters),
    params: {
      status:
        filter?.status !== 'all'
          ? statusToUpperCase[filter?.status as ShipmentStatus]
          : undefined,
      shipmentType: filter?.shipmentType,
      search: filter?.query,
      page: filter?.pageNumber,
      limit: filter?.pageSize,
      foNumbers: filter?.foNumbers,
      poNumbers: filter?.poNumbers,
      roNumbers: filter?.roNumbers,
      locationIds: filter?.locationIds,
      fromLocationIds: filter?.fromLocationIds,
      fromCustomerIds: filter?.fromCustomerIds,
      fromLocationCompanyTypes: filter?.fromLocationCompanyTypes,
      toLocationIds: filter?.toLocationIds,
      toCustomerIds: filter?.toCustomerIds,
      toLocationCompanyTypes: filter?.toLocationCompanyTypes,
      needsAttention: filter?.needsAttention ? true : undefined,
      quarantined: filter?.quarantined ? true : undefined,
      sortBy: filter?.sort ? sortByMap[filter.sort] : undefined,
      sortOrder: filter?.direction,
    },
  });

  return parseResponse(shipmentsResponseSchema, response.data, {
    schemaName: 'Shipments',
    endpoint: url,
  });
}
